import React from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  Database,
  FileSearch,
  ShieldAlert,
  Timer,
  Undo2,
} from "lucide-react";
import ToolLayout from "@/components/ToolLayout";
import useSEO from "@/hooks/useSEO";
import EarlyAccessForm from "@/components/EarlyAccessForm";

const TOOLS = [
  {
    path: "/tools/ai-governance-maturity",
    icon: ClipboardCheck,
    label: "AI Governance Maturity Assessment",
    description:
      "10-question quiz that scores your organisation across classification, review process, and governance domains. Get a Level 1–5 score and actionable gap analysis.",
    time: "~3 min",
  },
  {
    path: "/tools/contributor-class-classifier",
    icon: Database,
    label: "Contributor Class Classifier",
    description:
      "Describe a data source and get an instant Class 1, 2, or 3 classification with reasoning, review requirements, and policy flags to check before use in AI systems.",
    time: "~1 min",
  },
  {
    path: "/tools/policy-checker",
    icon: FileSearch,
    label: "Knowledge Artifact Policy Checker",
    description:
      "Paste any draft operational rule, macro, or escalation path. Get AI-powered flags for named-person phrasing, PII exposure, scope ambiguity, and compliance conflicts.",
    time: "~1 min",
  },
  {
    path: "/tools/review-sla-calculator",
    icon: Timer,
    label: "Review SLA Calculator",
    description:
      "Input your team size, weekly artifact volume, and target approval rate. Get a realistic SLA estimate, capacity utilisation score, and recommended team size.",
    time: "~1 min",
  },
  {
    path: "/tools/revocation-risk",
    icon: Undo2,
    label: "Revocation Risk Score",
    description:
      "Score any deployed knowledge artifact across four risk factors — age, source type, deployment reach, and review recency — and get a recommended action timeline.",
    time: "~1 min",
  },
];

export default function ToolsHub() {
  useSEO({
    title: "Free Enterprise AI Governance Tools — ProofMark",
    description:
      "Free tools for enterprise AI governance: maturity assessment, contributor class classifier, policy checker, review SLA calculator, and revocation risk scorer.",
    path: "/tools",
  });

  return (
    <ToolLayout>
      <div className="mb-12">
        <span className="tracked-caps">Free Tools</span>
        <h1 className="font-serif-display text-4xl mt-2 mb-3">
          Enterprise AI governance tools
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Five free utilities built on ProofMark&apos;s governance model. Use them to assess,
          classify, and audit the knowledge that goes into your AI systems.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.path}
              to={t.path}
              className="group border border-border rounded-lg p-6 bg-card hover:border-brand/50 hover:bg-muted/30 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="mt-0.5 p-2 rounded-md bg-muted">
                  <Icon className="h-5 w-5 text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <h2 className="font-medium text-foreground group-hover:text-brand transition-colors">
                      {t.label}
                    </h2>
                    <span className="text-xs text-muted-foreground shrink-0">{t.time}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t.description}</p>
                </div>
              </div>
            </Link>
          );
        })}

        {/* Product CTA card */}
        <div className="border border-brand/30 rounded-lg p-6 bg-brand/5 md:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="h-5 w-5 text-brand" />
            <span className="font-medium text-brand">ProofMark</span>
          </div>
          <p className="text-sm text-muted-foreground mb-4 max-w-xl">
            These tools are built on the same model that powers ProofMark — the governed
            review and evidence system for enterprise AI knowledge. Get early access to the full
            workflow: candidate extraction, human review, evidence chain, and revocation.
          </p>
          <EarlyAccessForm buttonText="Get early access" source="tools-hub" />
          <p className="mt-2 text-xs text-muted-foreground">No spam. Unsubscribe any time.</p>
        </div>
      </div>

      {/* Educational section */}
      <div className="mt-20 space-y-10 border-t border-border/80 pt-12">
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            Why enterprises need free governance tools
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            Most enterprise AI governance frameworks are either locked behind expensive consultants
            or buried in 200-page compliance documents. These tools translate the core concepts —
            contributor classification, policy checking, SLA planning, revocation risk — into
            interactive utilities you can use today, without a procurement cycle.
          </p>
        </section>
        <section>
          <h2 className="font-serif-display text-2xl mb-3">
            How these tools relate to each other
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            A complete knowledge governance workflow starts with classifying your sources
            (Contributor Class Classifier), runs every candidate through policy review (Policy
            Checker), sets realistic review timelines (SLA Calculator), assesses deployed artifacts
            for staleness (Revocation Risk), and benchmarks your overall programme maturity
            (Governance Maturity Assessment). ProofMark automates this end-to-end.
          </p>
        </section>
      </div>
    </ToolLayout>
  );
}
